/**
 * Create Project Dialog
 */

import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { useProjects } from '../../hooks/useProjects';

export type ProjectTab = 'documents' | 'presentations' | 'chat' | 'voice' | 'analysis' | 'legal' | 'cim' | 'financial-model' | 'sector-research';

interface CreateProjectDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onProjectCreated?: (projectId: string) => void;
}

export const CreateProjectDialog: React.FC<CreateProjectDialogProps> = ({
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
  onProjectCreated,
}) => {
  // Support both controlled and uncontrolled modes
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = (newOpen: boolean) => {
    if (externalOnOpenChange) {
      externalOnOpenChange(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [stage, setStage] = useState<'screening' | 'diligence' | 'ic' | 'closed' | 'passed'>('screening');
  const [enterpriseValue, setEnterpriseValue] = useState('');
  const [revenue, setRevenue] = useState('');
  const [ebitda, setEbitda] = useState('');

  const { createProject } = useProjects();

  const resetForm = () => {
    setName('');
    setCompanyName('');
    setIndustry('');
    setStage('screening');
    setEnterpriseValue('');
    setRevenue('');
    setEbitda('');
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (!name.trim()) {
      setError('Project name is required');
      setLoading(false);
      return;
    }

    if (!companyName.trim()) {
      setError('Company name is required');
      setLoading(false);
      return;
    }

    if (!industry.trim()) {
      setError('Industry is required');
      setLoading(false);
      return;
    }

    try {
      const projectId = await createProject({
        name,
        companyName,
        industry,
        stage,
        enterpriseValue: enterpriseValue ? parseFloat(enterpriseValue) : undefined,
        revenue: revenue ? parseFloat(revenue) : undefined,
        ebitda: ebitda ? parseFloat(ebitda) : undefined,
      });

      resetForm();
      setOpen(false);

      // Call callback with project ID for navigation
      if (onProjectCreated) {
        onProjectCreated(projectId);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="shadow-md hover:shadow-lg transition-smooth">
          <Plus className="mr-2 h-4 w-4" />
          New Project
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-4 border-b border-slate-200">
          <DialogTitle className="text-2xl font-bold text-slate-900">Create New Project</DialogTitle>
          <DialogDescription className="text-slate-600">
            Add a new due diligence project to your workspace
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive" className="mt-4 animate-in fade-in slide-in-from-top-2 duration-300">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid gap-5 py-6">
            {/* Basic Information Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Basic Information</h3>

              <div className="grid gap-2">
                <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                  Project Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  placeholder="Q1 2024 Investment Pipeline"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={loading}
                  className="h-11 transition-smooth focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="companyName" className="text-sm font-medium text-slate-700">
                  Company Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="companyName"
                  placeholder="Acme Corporation"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  disabled={loading}
                  className="h-11 transition-smooth focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="industry" className="text-sm font-medium text-slate-700">
                  Industry <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="industry"
                  placeholder="SaaS, Healthcare, Manufacturing, etc."
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  disabled={loading}
                  className="h-11 transition-smooth focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="stage" className="text-sm font-medium text-slate-700">Deal Stage</Label>
                <Select value={stage} onValueChange={(value: any) => setStage(value)}>
                  <SelectTrigger className="h-11 transition-smooth">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="screening">Screening</SelectItem>
                    <SelectItem value="diligence">Due Diligence</SelectItem>
                    <SelectItem value="ic">IC Review</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                    <SelectItem value="passed">Passed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Financial Metrics Section */}
            <div className="space-y-4 pt-4 border-t border-slate-200">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Financial Metrics (Optional)</h3>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="enterpriseValue" className="text-sm font-medium text-slate-700">
                    Enterprise Value
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                    <Input
                      id="enterpriseValue"
                      type="number"
                      step="0.1"
                      placeholder="50"
                      value={enterpriseValue}
                      onChange={(e) => setEnterpriseValue(e.target.value)}
                      disabled={loading}
                      className="h-11 pl-7 transition-smooth focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">M</span>
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="revenue" className="text-sm font-medium text-slate-700">
                    Revenue
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                    <Input
                      id="revenue"
                      type="number"
                      step="0.1"
                      placeholder="25"
                      value={revenue}
                      onChange={(e) => setRevenue(e.target.value)}
                      disabled={loading}
                      className="h-11 pl-7 transition-smooth focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">M</span>
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="ebitda" className="text-sm font-medium text-slate-700">
                    EBITDA
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                    <Input
                      id="ebitda"
                      type="number"
                      step="0.1"
                      placeholder="8"
                      value={ebitda}
                      onChange={(e) => setEbitda(e.target.value)}
                      disabled={loading}
                      className="h-11 pl-7 transition-smooth focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">M</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0 pt-4 border-t border-slate-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={loading}
              className="transition-smooth"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="shadow-md hover:shadow-lg transition-smooth"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </span>
              ) : (
                'Create Project'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
